import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useUser } from 'contexts/UserContext';
import {
    DataSource,
    DataSourceOptions,
    DataSourcePaging,
    DataSourceQueryResult,
    DataSourceSort,
    firstPage,
    ItemKeyCallback,
    replaceItems,
} from 'custom_hooks/data_hooks/data_source/data_source';
import { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { queryClient } from 'utils/query';

import { useDataSourceParams } from './useDataSourceParams';

/**
 *
 * @param result The query result data to update.
 * @param items Updated item list.
 * @param itemKey Callback used to uniquely identify an item (used for replacing items).
 * @returns The updated query result data.
 */
export function replaceItemsInPagedResult<TData>(
    result: DataSourceQueryResult<TData> | undefined,
    items: TData[],
    itemKey: ItemKeyCallback<TData>
): DataSourceQueryResult<TData> | undefined {
    return items.length > 0 && result && result.results.length > 0
        ? {
              ...result,
              results: replaceItems(result.results, items, itemKey),
          }
        : result;
}

export type PagedDataSource<TData, TFilter> = DataSource<TData, TFilter> &
    Readonly<{
        /**
         * Used to change the current page.
         *
         * @param pageValue A page number or state setter function.
         *
         * @example:
         * data.setPage(10);
         * data.setPage((page) => page + 1);
         */
        setPage: (pageValue: SetStateAction<number>) => void;

        /**
         * Reloads the current page of data.
         */
        reloadPage: () => void;
    }>;

/**
 * Initializes a paged data source.
 *
 * @param param0 Configuration options.
 */
export function usePagedDataSource<TData, TFilter>({
    name,
    enabled = true,
    defaultFilter,
    defaultSort = [],
    pageLimit: overridePageSize,
    query,
    retry,
}: DataSourceOptions<TData, TFilter>): PagedDataSource<TData, TFilter> {
    const nameRef = useRef(name);
    const defaultFilterRef = useRef(defaultFilter);
    defaultFilterRef.current = defaultFilter;
    const defaultSortRef = useRef(defaultSort);
    defaultSortRef.current = defaultSort;

    const [filter, setFilter] = useState(defaultFilter);
    const [sort, setSort] = useState(defaultSort);
    const [page, setPage] = useState(firstPage);

    // Use the shared hook for filter parameter updates
    useDataSourceParams(defaultFilter, setFilter, nameRef.current);

    const { pageSize: userPageSize } = useUser();
    const pageSize = useMemo(() => overridePageSize ?? userPageSize, [
        overridePageSize,
        userPageSize,
    ]);

    // reset to the first page when page size changes
    useEffect(() => {
        setPage(firstPage);
    }, [pageSize]);

    const { isFetching, data, error, refetch } = useQuery({
        queryKey: [nameRef.current, filter, sort, page, pageSize],

        queryFn: async () =>
            query({
                filter,
                sort,
                page,
                pageLimit: pageSize,
            }),

        placeholderData: keepPreviousData,
        enabled,
        retry,
    });

    const paging = useMemo<DataSourcePaging>(
        () => ({
            page,
            pageLimit: pageSize,
            totalPages: data?.totalPages,
            totalResults: data?.totalResults,
        }),
        [data?.totalPages, data?.totalResults, page, pageSize]
    );

    const reset = useCallback(() => {
        setFilter({ ...defaultFilterRef.current });
        setSort([...defaultSortRef.current]);
        setPage(firstPage);
    }, []);

    const reloadPage = useCallback(() => refetch(), [refetch]);

    const setFilterAndPage = useCallback((filterValue: SetStateAction<TFilter>) => {
        setFilter(filterValue);
        setPage(firstPage);
    }, []);

    const setSortAndPage = useCallback((sortValue: SetStateAction<DataSourceSort[]>) => {
        setSort(sortValue);
        setPage(firstPage);
    }, []);

    const updateData = useCallback(
        (items: TData[], itemKey: ItemKeyCallback<TData>) => {
            queryClient.setQueryData<DataSourceQueryResult<TData> | undefined>(
                [nameRef.current, filter, sort, page, pageSize],
                (current) => replaceItemsInPagedResult(current, items, itemKey)
            );
        },
        [filter, page, pageSize, sort]
    );

    const dataSource: PagedDataSource<TData, TFilter> = {
        filter,
        sort,
        isLoading: isFetching,
        error,
        paging,
        results: data?.results,
        setFilter: setFilterAndPage,
        setSort: setSortAndPage,
        setPage,
        reloadPage,
        reset,
        updateData,
    };

    return dataSource;
}
